import { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import LoginChecker from './LoginChecker';
import FlashMessageError from './platform/components/FlashMessageError/FlashMessageError';
import LoadingScreen from './platform/components/LoadingScreen/LoadingScreen';
import ActivityPage from './platform/pages/ActivityPage/ActivityPage';
import ChooseLanguageFromPage from './platform/pages/ChooseLanguageFromPage/ChooseLanguageFromPage';
import HomePage from './platform/pages/HomePage/HomePage';
import LessonsPage from './platform/pages/LessonsPage/LessonsPage';
import MainPage from './platform/pages/MainPage/MainPage';
import TestPage from './platform/pages/TestPage/TestPage';
import Store from './Store';

import AboutPage from './product/pages/AboutPage';
import OrderPage from './product/pages/OrderPage';
import ProductPage from './product/pages/ProductPage';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import AdminPages from './admin/pages';
import { LanguageContext } from './common/contexts/LanguageContext';
import { ProductWrapper } from './common/productWrapper/ProductWrapper';
import ConsultantPages from './consultant/ConsultantPages';
import { ConsultantHintContextWrapper } from './platform/contexts/ConsultantHintContext';
import { DownloadCertificatePage } from './platform/pages/DownloadCertificatePage/DownloadCertificatePage';
import MailBoxPage from './platform/pages/MailBoxPage/MailBoxPage';
import StatsWrapper from './platform/stats/StatsWrapper';
import { UTM } from './product/constants/utm';
import { croatian } from './product/data/croatianLanguage';
import { czech } from './product/data/czechLanguage';
import { hungary } from './product/data/hungaryLanguage';
import { lithuanian } from './product/data/lithuanianLanguage';
import { polish } from './product/data/polishLanguage';

import ReviewPage from './product/pages/ReviewPage/ReviewPage';
import ThankYouPage from './product/pages/ThankYouPage/ThankYouPage';
import { latvian } from './product/data/lotvianLanguage';

const translations = {
  lt: lithuanian,
  pl: polish,
  hr: croatian,
  cz: czech,
  lv: latvian
};

function App() {
  const [errors, setErrors] = useState([]);
  const { lang } = useContext(LanguageContext);
  const [dataByLanguage, setDataByLanguage] = useState(hungary);
  const location = useLocation().pathname;
  const [searchParams] = useSearchParams();

  useLayoutEffect(() => {
    if (lang) {
      const data = translations[lang] ?? czech;

      setDataByLanguage(data);
      Store.setUserLanguage(lang);
      Store.setUserPlatformLanguage(lang);
    }
  }, [lang]);

  useEffect(() => {
    Store.setItem(UTM.SOURCE, searchParams.get(UTM.SOURCE));
    Store.setItem(UTM.MEDIUM, searchParams.get(UTM.MEDIUM));
  }, []);

  async function handleError(errors, exceptionMessage = null) {
    if (exceptionMessage) return setErrors([exceptionMessage]);
    const errCodesArray = errors.split(',');
    const errorMessagesArray = [];
    for (let i = 0; i < errCodesArray.length; i++) {
      errorMessagesArray.push(
        await Store.getExceptionMessage(errCodesArray[i])
      );
    }
    setErrors(errorMessagesArray);
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div className='App'>
      <ConsultantHintContextWrapper>
        <LoginChecker />
        <StatsWrapper>
          {errors.length > 0 && (
            <FlashMessageError setErrors={setErrors} messages={errors} />
          )}

          <LoadingScreen />
          <Routes>
            <Route
              exact
              path='/consultant/*'
              element={<ConsultantPages setError={handleError} />}
            ></Route>
            <Route
              exact
              path='/platform'
              element={<ChooseLanguageFromPage setError={handleError} />}
            ></Route>
            <Route
              exact
              path='/platform/admin/*'
              element={<AdminPages setError={handleError} />}
            ></Route>
            <Route
              exact
              path='/platform/:languageShort'
              element={<MainPage setError={handleError} />}
            ></Route>
            <Route
              exact
              path='/platform/lessons/:pairId/:level'
              element={<LessonsPage setError={handleError} />}
            ></Route>
            <Route
              exact
              path='/platform/activity/:pairId/:level'
              element={<ActivityPage setError={handleError} />}
            ></Route>
            <Route
              exact
              path='/platform/activity/:pairId/:level/:lessonId'
              element={<ActivityPage setError={handleError} />}
            ></Route>
            <Route
              exact
              path='/platform/test/:pairId/:level'
              element={<TestPage setError={handleError} />}
            ></Route>
            <Route
              exact
              path='/platform/mail-box'
              element={<MailBoxPage setError={handleError} />}
            ></Route>
            <Route
              exact
              path='/certificate'
              element={<DownloadCertificatePage setError={handleError} />}
            ></Route>
            <Route
              exact
              path='/:lang'
              element={
                <ProductWrapper
                  footerData={dataByLanguage.footer}
                  navbarData={dataByLanguage.navbar.data}
                >
                  <HomePage setError={handleError} />
                </ProductWrapper>
              }
            ></Route>
            <Route
              exact
              path='/:lang/password-recovery'
              element={
                <ProductWrapper
                  footerData={dataByLanguage.footer}
                  navbarData={dataByLanguage.navbar.data}
                >
                  <HomePage
                    isInitPasswordRecoveryModalOpen={true}
                    setError={handleError}
                  />
                </ProductWrapper>
              }
            ></Route>
            <Route
              exact
              path='/:lang/change-password'
              element={
                <ProductWrapper
                  footerData={dataByLanguage.footer}
                  navbarData={dataByLanguage.navbar.data}
                >
                  <HomePage
                    isInitChangePasswordModalOpen={true}
                    setError={handleError}
                  />
                </ProductWrapper>
              }
            ></Route>
            <Route
              exact
              path='/:lang/product'
              element={
                <ProductWrapper
                  footerData={dataByLanguage.footer}
                  navbarData={dataByLanguage.navbar.data}
                >
                  <ProductPage data={dataByLanguage.mainPage} />
                </ProductWrapper>
              }
            />
            <Route
              exact
              path='/:lang/review'
              element={
                <ProductWrapper
                  footerData={dataByLanguage.footer}
                  navbarData={dataByLanguage.navbar.data}
                >
                  <ReviewPage data={dataByLanguage.reviews} />
                </ProductWrapper>
              }
            />
            <Route
              exact
              path='/:lang/thankyou'
              element={
                <ProductWrapper
                  footerData={dataByLanguage.footer}
                  navbarData={dataByLanguage.navbar.data}
                >
                  <ThankYouPage data={dataByLanguage.reviews} />
                </ProductWrapper>
              }
            />
            <Route
              exact
              path='/:lang/about'
              element={
                <ProductWrapper
                  footerData={dataByLanguage.footer}
                  navbarData={dataByLanguage.navbar.data}
                >
                  <AboutPage data={dataByLanguage.aboutUs} />
                </ProductWrapper>
              }
            />
            <Route
              exact
              path='/:lang/course'
              element={
                <ProductWrapper
                  footerData={dataByLanguage.footer}
                  navbarData={dataByLanguage.navbar.data}
                >
                  <OrderPage data={dataByLanguage.formSender} />
                </ProductWrapper>
              }
            />
            <Route
              path='/'
              element={
                <ProductWrapper
                  footerData={dataByLanguage.footer}
                  navbarData={dataByLanguage.navbar.data}
                ></ProductWrapper>
              }
            />
          </Routes>
        </StatsWrapper>
      </ConsultantHintContextWrapper>
    </div>
  );
}

export default App;
